import { toast } from "sonner";
import type { components } from "@/api/schema";
import {
  CARE_OF_KINDS,
  useSetSpecialProgramSupplier,
  type SourcingOption,
} from "@/api/sourcing";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDateOnly } from "@/lib/utils";

type Program = components["schemas"]["ManufacturerSpecialProgramSummary"];

/**
 * The manufacturer's specials programs that apply through this source: ones tied to its
 * supplier or buying group, and, on a direct source, ones that run through every direct
 * source. A distributor or online retailer sells at its own prices, so only programs tied
 * to it show there.
 */
export function SourceSpecials({
  manufacturerUuid,
  option,
  preferred,
  programs,
}: {
  manufacturerUuid: string;
  option: SourcingOption;
  preferred: boolean;
  programs: Program[];
}) {
  const setSupplier = useSetSpecialProgramSupplier(manufacturerUuid);
  const parties = [option.supplier, option.buying_group].filter(
    (party): party is NonNullable<typeof party> => party != null,
  );
  const partyIds = new Set(parties.map((party) => party.id));
  const direct = !option.supplier || !CARE_OF_KINDS.has(option.supplier.kind);
  const visible = programs.filter((program) =>
    program.supplier_id == null ? direct : partyIds.has(program.supplier_id),
  );
  if (visible.length === 0) return null;

  return (
    <section aria-labelledby={`source-specials-${option.id}`}>
      <h3
        id={`source-specials-${option.id}`}
        className="mb-2 text-sm font-semibold"
      >
        Specials
      </h3>
      <ul className="divide-y rounded-lg border">
        {visible.map((program) => {
          const applies = preferred && !program.on_hold;
          return (
            <li
              key={program.program}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm"
            >
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-x-2 font-medium">
                  {program.promotion ?? program.program}
                  <Badge variant={applies ? "default" : "secondary"}>
                    {applies ? "Applies" : "On hold"}
                  </Badge>
                </span>
                <span className="block text-xs text-muted-foreground">
                  {formatDateOnly(program.starts_on)} –{" "}
                  {formatDateOnly(program.ends_on)} · {program.specials}{" "}
                  {program.specials === 1 ? "special" : "specials"}
                </span>
              </span>
              <Select
                value={
                  program.supplier_id != null
                    ? String(program.supplier_id)
                    : "any"
                }
                onValueChange={(value) =>
                  setSupplier.mutate(
                    {
                      program: program.program,
                      supplierId: value === "any" ? null : Number(value),
                    },
                    {
                      onError: () =>
                        toast.error(
                          "Could not change where this program applies",
                        ),
                    },
                  )
                }
                disabled={setSupplier.isPending}
              >
                <SelectTrigger
                  aria-label={`${program.program} runs through`}
                  className="h-8 w-48"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Every direct source</SelectItem>
                  {parties.map((party) => (
                    <SelectItem key={party.id} value={String(party.id)}>
                      Only through {party.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
